/**
 * Which legal-document language a request resolves to, and how that one language
 * is loaded — **without** pulling the other four in with it.
 *
 * The static map in `./index.ts` imports all five bundles, which is what tests
 * and tooling want and exactly what a browser must not get: bundled statically,
 * the five translations land in the client entry chunk and every visitor to the
 * home page downloads tens of kilobytes of legal text they will never open. Here the
 * bundles are behind dynamic imports, so Vite emits one chunk per language and
 * the route loader fetches precisely one.
 *
 * Anything that renders in the browser imports from THIS module, never from the
 * barrel.
 */

import {
  CONTENT_LANG_ENDONYM,
  CONTENT_LANGS,
  type ContentLang,
  contentLangOf,
} from "@fazly/shared/contentLang";

import type { LegalBundle } from "./types";

/** One dynamic import per language — the whole point of this module. */
const LOADERS: Record<ContentLang, () => Promise<LegalBundle>> = {
  en: () => import("./en").then((m) => m.en),
  es: () => import("./es").then((m) => m.es),
  pt: () => import("./pt").then((m) => m.pt),
  tr: () => import("./tr").then((m) => m.tr),
  de: () => import("./de").then((m) => m.de),
};

export const LEGAL_LANGS = CONTENT_LANGS;
export const LEGAL_LANG_ENDONYM = CONTENT_LANG_ENDONYM;
export const DEFAULT_LEGAL_LANG: ContentLang = "en";

/**
 * Which document language to show for a `?lang=` value (or a browser locale).
 *
 * Falls back to English rather than 404-ing: these pages are linked from the App
 * Store listing and from inside the app, and a legal page that refuses to render
 * because of a query string is worse than one in the wrong language.
 */
export function resolveLegalLang(raw: string | null | undefined): ContentLang {
  return contentLangOf(raw) ?? DEFAULT_LEGAL_LANG;
}

/**
 * The URL one legal document lives at in one language.
 *
 * English is the bare path and the other four carry `?lang=`, for the same
 * reason the landing page keeps English at the root: `/privacy` is what the App
 * Store listing, the in-app rows and every existing link point at, and it must
 * not become a second URL for a page that already has one.
 *
 * ── This is what fixed the hreflang ──────────────────────────────────────────
 *
 * The pages used to declare `hreflang="en" href="/privacy?lang=en"` while
 * canonicalising to `/privacy`, and every `?lang=` variant canonicalised to
 * `/privacy` too. Both halves of that are disqualifying on their own:
 *
 *  - a cluster whose members do not include their own canonical URL is ignored
 *    outright, so all five annotations did nothing; and
 *  - a Spanish page canonicalising to the English one asks Google to drop the
 *    Spanish page, which it does.
 *
 * With this helper the two agree: `hreflang` names the same URL the page
 * canonicalises to, in every language.
 */
export function legalPath(
  doc: "/privacy" | "/terms",
  lang: ContentLang,
): string {
  return lang === DEFAULT_LEGAL_LANG ? doc : `${doc}?lang=${lang}`;
}

/** Load one language's documents. */
export function loadLegalBundle(lang: ContentLang): Promise<LegalBundle> {
  return LOADERS[lang]();
}

export type { ContentLang };
